import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { ActionDialog } from '@/common/dialog/ActionDialog';
import { useDialog } from '@/common/dialog/DialogContext';
import { ExportDialog } from '@/common/dialog/ExportDialog';
import { useEntityChangeSubscr } from '@/common/entityChange/EntityChangeContext';
import { TargetKpiSelect } from '@/common/entityFramework/entityPerspective/overwrite/TargetKpiSelect';
import { Grid } from '@/common/grid/Grid';
import { useDependentKey } from '@/common/hooks/key/useDependentKey';
import { useEntityFromRoute } from '@/common/hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { ContentBox } from '@/common/layout/ContentBox';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Split } from '@/common/split/Split';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { TreeUtils } from '../../utils/TreeUtils';
import { EntityBanner } from '../entityBanner/EntityBanner';
import { useTab } from '../entityTabs/TabContext';
import { EntityTitle } from '../entityTitle/EntityTitle';
import { DimRearrangeButton } from './support/DimRearrangeButton';
import { DimTree } from './support/DimTree';
import { FilterSummary } from './support/FilterSummary';
import { TrendDimsButton } from './support/TrendDimsButton';
import { ViewSelect } from './support/ViewSelect';

/**
 * FDN-244 (Perspective)
 */
export const EntityPerspective = ({
  // Optional Module and Entity. If not provided they will be grabbed from the route
  module,
  entity,

  // Views
  views,

  // Optional Modifications to EntityPerspective
  augReqParam,
  rowIconsFn,
  augButtons,
  augKpis,
  defaultDimFilters: defaultDimFiltersProp = {},

  // Optional Modifications to the Grid
  gridOptions = {},
  events = {},

  // Optional Listener to the Reset View Button
  onViewReset,

  // Ref
  ref,
}) => {
  // Module and Entity
  const routeModule = useModuleFromRoute();
  module ??= routeModule;
  const routeEntity = useEntityFromRoute();
  entity ??= routeEntity;

  // Context
  const { navState, assign, navigateTo } = useNavState();
  const { tabId } = useTab();
  const rsrc = useRsrc();

  // NavState
  const perspectiveNavStateId = tabId ? tabId + 'PerspectiveState' : 'perspectiveState';
  const perspectiveNavState = navState[perspectiveNavStateId] ?? {};

  // Ref
  const gridRef = useRef(null);

  // State
  const [activeView, setActiveView] = useState(
    perspectiveNavState.viewName ? views.find((view) => view.name === perspectiveNavState.viewName) : views[0],
  );
  const [activeDims, setActiveDims] = useState([]);
  const [activeTrendSettings, setActiveTrendSettings] = useState();
  const [activeKpis, setActiveKpis] = useState(); // Array of KPI codes currently shown (subset of the view's KPIs)
  const [perspectiveDef, setPerspectiveDef] = useState();

  const [fieldDefs, setFieldDefs] = useState();
  const [exportReq, setExportReq] = useState();
  const [entityDef, setEntityDef] = useState();
  const [dimFilters, setDimFilters] = useState({}); // Object of dimCode to a Set of selected Entity IDs for that dim
  const [dimToPeriodicities, setDimToPeriodicities] = useState(); // Object of dimCode to an array of selected Periodicities
  const [trendValues, setTrendValues] = useState(); // { dimCode: [{ id, label }, ...] } — discovered once at mount

  const [showFilterSummary, setShowFilterSummary] = useState(perspectiveNavState.showFilterSummary ?? false);
  const [splitSizes, setSplitSizes] = useState(perspectiveNavState.splitSizes ?? [75, 25]);

  // ----- Initialization -----

  useEffect(() => {
    let defPluginReq = rsrc.data(module, 'PERSPECTIVE_DEF_DATA_PLUGIN', { entity }, entity);
    let entityDefReq = rsrc.entityDef(module, entity);

    // Union of trend dim codes across ALL views — used for the one-shot discovery call at mount
    const allViewsTrendDims = [
      ...new Set((views ?? []).flatMap((view) => (view.trends ?? []).flatMap((trend) => trend.dims))),
    ];
    let trendValuesReq =
      allViewsTrendDims.length > 0 ?
        rsrc.data(module, 'PERSPECTIVE_TREND_VALUES_DATA_PLUGIN', { entity, trendDims: allViewsTrendDims }, entity)
      : Promise.resolve({});

    Promise.all([defPluginReq, entityDefReq, trendValuesReq]).then(([perspectiveDef, entityDef, trendValues]) => {
      setPerspectiveDef(perspectiveDef);
      setEntityDef(entityDef);
      setTrendValues(trendValues);
    });
  }, []);

  useEffect(() => {
    if (!perspectiveDef) return;
    // Handle and set activeDims
    const disabledDims = activeView.disabledDims ?? [];
    Object.values(perspectiveDef.codeToDim).forEach((dim) => {
      dim.id = StringUtils.toLowerCamel(dim.code);
      if (disabledDims.includes(dim.code)) {
        dim.disabled = true;
      }
    });

    resetDims();
    resetPeriodicities();
    resetTrendSettings();
    resetKpis();

    // Restore state if applicable
    if (perspectiveNavState.dimCodes) {
      setDims(perspectiveNavState.dimCodes);
    }

    if (perspectiveNavState.dimFilters) {
      const dimFilters = defaultDimFilters();
      setDimFilters({ ...dimFilters, ...perspectiveNavState.dimFilters });
    }

    if (perspectiveNavState.dimToPeriodicities) {
      setDimToPeriodicities(perspectiveNavState.dimToPeriodicities);
    }

    if (perspectiveNavState.trendSettings) {
      setActiveTrendSettings(perspectiveNavState.trendSettings);
    }

    if (perspectiveNavState.activeKpis) {
      setActiveKpis(perspectiveNavState.activeKpis);
    }
  }, [perspectiveDef]);

  useEffect(() => {
    if (!activeTrendSettings || !activeKpis) return;
    buildAndSetFieldDefs();
  }, [activeTrendSettings, activeKpis]);

  const { refreshTrigger } = useEntityChangeSubscr(module, entity);

  // ----- Column Definitions -----

  const buildAndSetFieldDefs = () => {
    const activeKpiSet = new Set(activeKpis);
    const kpis = activeView.kpis
      .filter((kpiCode) => activeKpiSet.has(kpiCode))
      .map((kpiCode) => perspectiveDef.codeToKpi[kpiCode]);

    const grpFieldDefs = {};
    const fieldDefs = [
      {
        displayName: '',
        colId: 'dimCol',
        field: 'node',
        suppressSizeToFit: true,
        valueFormatter: null,
        autoGrouping: true,
        cellRenderer: 'perspectiveDimCellRenderer',
        cellClass: 'align-left',
        pinned: 'left',
        filter: null,
        sortable: false,
        lockPosition: 'left',
        rowIconsFn, // Supply the rowIcons function in the colDef for the cellRenderer
      },
    ];

    kpis.forEach((kpi) => {
      kpi.field = StringUtils.toLowerCamel(kpi.code);

      if (kpi.cellRendererCode && kpi.cellRendererCode !== 'NONE') {
        kpi.cellRenderer = StringUtils.toLowerCamel(kpi.cellRendererCode) + 'CellRenderer';
      }

      kpi.filter = false;
      kpi.sortable = false;

      if (augKpis) augKpis(kpi);

      const grpName = kpi.grpName;
      if (grpName) {
        let grpFieldDef = grpFieldDefs[grpName];
        if (grpFieldDef == null) {
          grpFieldDef = { displayName: grpName, groupId: grpName, children: [] };
          grpFieldDefs[grpName] = grpFieldDef;
          fieldDefs.push(grpFieldDef);
        }
        grpFieldDef.children.push(kpi);
      } else {
        fieldDefs.push(kpi);
      }
    });

    // Apply trends. Each trend transforms the fieldDefs tree: matched KPI leaves are expanded
    // into nested dim-value groups based on layout. augKpis already ran on the base KPI, so
    // any customer mutations (cellRenderer, valueFormatter, etc.) ride along into every trend
    // cell via the shallow clone.
    let transformedDefs = fieldDefs;
    (activeTrendSettings.trends ?? []).forEach((trend) => {
      transformedDefs = applyTrend(transformedDefs, trend);
    });

    setFieldDefs(transformedDefs);
  };

  // Apply a single trend's transform to the colDefs tree.
  const applyTrend = (defs, trend) => {
    if (trend.layout === 'KPI_OVER_DIM') {
      return defs.map((def) => expandKpiOverDim(def, trend));
    }
    return spliceDimOverKpi(defs, trend);
  };

  // KPI_OVER_DIM: recurse through groups; replace each matched leaf KPI with a wrapper group
  // (KPI displayName) containing nested dim-value groups (one level per trend dim). Composes
  // cleanly with grpName: the grpName group stays as the outermost wrapper.
  // A trailing "Total" leaf is appended unless `trend.total` is explicitly false. The total
  // reuses the original un-suffixed field, which the backend already populates as the grand
  // total via the empty GROUPING SETS row. With multiple dims, buildTrendColumns also inserts a
  // per-group subtotal "Total" leaf at each inner level (e.g. a total per channel across prods).
  const expandKpiOverDim = (def, trend) => {
    if (def.children) {
      return { ...def, children: def.children.map((child) => expandKpiOverDim(child, trend)) };
    }
    if (def.trends || !trend.kpis.includes(def.code)) return def;

    const children = buildTrendColumns(
      trend,
      0,
      '',
      {},
      (val, suffix, trends) => ({
        ...def,
        displayName: val.label,
        field: def.field + suffix,
        trends,
      }),
      trend.total !== false,
    );

    if (trend.total !== false) {
      children.push({ ...def, displayName: 'Total', trends: {} });
    }

    return { displayName: def.displayName, children };
  };

  // DIM_OVER_KPI: lift matched KPI leaves out of their original positions and place them under
  // a single outer dim-value hierarchy at the deepest scope common to every match. Same-scope
  // matches (e.g., all in one grpName) end up as bare KPI leaves under each dim value; cross-
  // scope matches end up wrapped in their original grpName(s) per dim value so the labels are
  // preserved. Wrappers emptied by the lift are pruned; wrappers with non-trended siblings
  // survive in place alongside the spliced dim group.
  const spliceDimOverKpi = (defs, trend) => {
    const collected = [];
    const collect = (nodes, path) => {
      nodes.forEach((def) => {
        if (def.children) collect(def.children, [...path, def]);
        else if (!def.trends && trend.kpis.includes(def.code)) collected.push({ kpi: def, parentPath: path });
      });
    };
    collect(defs, []);
    if (collected.length === 0) return defs;

    // Deepest scope shared by every match (longest common prefix of parent paths).
    let commonLen = collected[0].parentPath.length;
    for (let i = 1; i < collected.length; i++) {
      const p = collected[i].parentPath;
      let j = 0;
      while (j < commonLen && j < p.length && collected[0].parentPath[j] === p[j]) j++;
      commonLen = j;
    }

    // Reconstruct the path BELOW commonLen for each matched KPI. Same-scope matches collapse to
    // bare leaves (rel is empty); cross-scope matches end up grouped under their relative wrappers.
    const buildInner = (items, depth, suffix, trends) => {
      const order = [];
      const map = new Map();
      items.forEach((item) => {
        const rel = item.parentPath.slice(commonLen);
        if (depth < rel.length) {
          const grpDef = rel[depth];
          if (!map.has(grpDef)) {
            const entry = { groupDef: grpDef, items: [] };
            map.set(grpDef, entry);
            order.push(entry);
          }
          map.get(grpDef).items.push(item);
        } else {
          order.push({ kpi: item.kpi });
        }
      });
      return order.map((o) => {
        if (o.kpi) return { ...o.kpi, field: o.kpi.field + suffix, trends };
        return { ...o.groupDef, children: buildInner(o.items, depth + 1, suffix, trends) };
      });
    };

    const dimGroups = buildTrendColumns(
      trend,
      0,
      '',
      {},
      (val, suffix, trends) => ({
        displayName: val.label,
        children: buildInner(collected, 0, suffix, trends),
      }),
      trend.total === true,
    );

    // For DIM_OVER_KPI, totals are opt-in via `trend.total === true`. A "Total" outer group
    // sits to the right of the dim-value groups, mirroring the KPI subtree but pointing at the
    // original un-suffixed fields (already populated by the backend's grand-total row).
    if (trend.total === true) {
      dimGroups.push({ displayName: 'Total', children: buildInner(collected, 0, '', {}) });
    }

    // Strip matched leaves from a subtree, pruning groups that end up empty.
    const liftMatches = (nodes) => {
      const result = [];
      nodes.forEach((def) => {
        if (def.children) {
          const pruned = liftMatches(def.children);
          if (pruned.length > 0) result.push({ ...def, children: pruned });
        } else if (def.trends || !trend.kpis.includes(def.code)) {
          result.push(def);
        }
      });
      return result;
    };

    // Walk down to commonLen following the shared ancestor chain, then splice. The splice
    // position is the last index that contained a match (so surviving sibling-only stubs stay
    // in their original visual position relative to the dim group).
    const rewrite = (nodes, depth) => {
      if (depth < commonLen) {
        return nodes.map((def) => {
          if (def.children && collected[0].parentPath[depth] === def) {
            return { ...def, children: rewrite(def.children, depth + 1) };
          }
          return def;
        });
      }
      const result = [];
      let spliceIdx = -1;
      nodes.forEach((def) => {
        if (def.children) {
          const hadMatch = collected.some((c) => c.parentPath[depth] === def);
          const pruned = liftMatches(def.children);
          if (pruned.length > 0) result.push({ ...def, children: pruned });
          if (hadMatch) spliceIdx = result.length;
        } else if (!def.trends && trend.kpis.includes(def.code)) {
          spliceIdx = result.length;
        } else {
          result.push(def);
        }
      });
      if (spliceIdx < 0) spliceIdx = result.length;
      return [...result.slice(0, spliceIdx), ...dimGroups, ...result.slice(spliceIdx)];
    };

    return rewrite(defs, 0);
  };

  // Resolve the ordered list of buckets (trend columns) for one trend dim. A bucket is either a
  // raw discovered value or a value group that merges several ids under a single column.
  //   - explicit trend.values[dimCode] wins (a hand-picked selection / order) as an ordered list
  //     of { key, label }, where `key` is a group key or a raw id; member ids are resolved back
  //     from trendValueGroups for drill-through;
  //   - otherwise the dim's value groups overlay the discovered values: each group becomes one
  //     bucket absorbing its member ids, and ungrouped discovered values remain their own
  //     buckets (buckets = groups ∪ (discovered − grouped)).
  // Each bucket is { label, key, filter } where `key` forms the backend-pivoted field suffix and
  // `filter` is the id (raw) or id list (group) used for trend-cell drill-through.
  const dimBuckets = (dimCode, trend) => {
    const groups = activeTrendSettings.trendValueGroups?.[dimCode] ?? [];
    // Group lookup keyed by the same upper-camel key used for field suffixes, so an explicit
    // value referencing a group resolves back to that group's member ids for drill-through.
    const groupsByKey = new Map(groups.map((group) => [StringUtils.toUpperCamel(group.key), group]));

    // Explicit per-trend selection (ordered { key, label }). `key` is a group key or a raw id;
    // `filter` (member ids for a group, or the raw id) rides along for drill-through.
    const explicit = trend.values?.[dimCode];
    if (explicit) {
      return explicit.map(({ key, label }) => {
        const group = groupsByKey.get(key);
        return { label, key, filter: group ? group.ids : key };
      });
    }

    const groupedIds = new Set(groups.flatMap((group) => group.ids));
    const discovered = trendValues[dimCode] ?? [];

    const groupBuckets = groups.map((group) => ({
      label: group.label,
      key: StringUtils.toUpperCamel(group.key),
      filter: group.ids,
    }));
    const rawBuckets = discovered
      .filter((val) => !groupedIds.has(val.id))
      .map((val) => ({ label: val.label, key: val.id, filter: val.id }));

    return [...groupBuckets, ...rawBuckets];
  };

  // Build N levels of dim-value column groups (N = trend.dims.length). At the deepest level, calls
  // makeLeaf(bucket, suffix, trends) to produce the actual leaf node. `suffix` accumulates upper-
  // camel dim code + bucket key pairs (e.g., 'EarnedPeriod42' or 'EarnedPeriodRecentThree') for
  // field naming; `trends` accumulates { dimCode: bucketFilter, ... } metadata used by leaf
  // filtering / drill-down, where bucketFilter is a raw id or a group's id list.
  // When `total` is set and the trend has multiple dims, each non-leaf group gets a trailing
  // "Total" leaf carrying its partial suffix/trends — a subtotal across the inner dim(s), which
  // the backend already populates via the matching partial GROUPING SET row.
  const buildTrendColumns = (trend, depth, suffix, trends, makeLeaf, total) => {
    const dimCode = trend.dims[depth];
    const buckets = dimBuckets(dimCode, trend);
    const isLast = depth === trend.dims.length - 1;

    return buckets.map((bucket) => {
      const nextSuffix = suffix + StringUtils.toUpperCamel(dimCode) + bucket.key;
      const nextTrends = { ...trends, [dimCode]: bucket.filter };
      if (isLast) return makeLeaf(bucket, nextSuffix, nextTrends);
      const children = buildTrendColumns(trend, depth + 1, nextSuffix, nextTrends, makeLeaf, total);
      if (total) {
        children.push(makeLeaf({ label: 'Total' }, nextSuffix, nextTrends));
      }
      return {
        displayName: bucket.label,
        children,
      };
    });
  };

  // ----- Fetch -----

  const onGridReady = () => {
    serverSideDatasource();
  };

  const serverSideDatasource = () => {
    const dataSource = {
      getRows: (params) => {
        const reqParam = buildReqParam(params);
        if (reqParam.level === 0) {
          buildAndSetExportReq(reqParam);
        }

        return rsrc.data(module, 'PERSPECTIVE_FETCH_DATA_PLUGIN', reqParam, entity).then((res) => {
          params.success({ rowData: res, rowCount: res.length });
        });
      },
    };
    gridRef.current.setDataSource(dataSource);
  };

  const buildAndSetExportReq = (reqParam) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      displayName: entityDef.displayName,
      module,
      entity,
      fetchReqParam: reqParam,
      activeDims: activeDims.map((dim) => dim.code),
      fieldDefs,
      uiType: 'PERSPECTIVE',
    };

    setExportReq(exportReq);
  };

  const isServerSideGroup = (node) => {
    return !node.isLeaf;
  };

  const getServerSideGroupKey = (node) => {
    return node.path;
  };

  const refreshGrid = () => {
    serverSideDatasource();
  };

  useEffect(() => {
    if (!gridRef.current || !gridRef.current.gridApiRef.current) return;
    refreshGrid();
  }, [refreshTrigger, dimFilters, dimToPeriodicities, activeDims, augReqParam]);

  const buildReqParam = useCallback(
    (params) => {
      const isFetch = !!params.parentNode;

      const reqParam = {};

      // Entity
      reqParam.entity = entity;

      // View
      reqParam.view = activeView.name;

      // Level
      // Parent node is the node we want the data underneath.
      // In the fetch scenario, parentNode is params.parentNode. However, in the navigation scenario, params itself is the parentNode
      // as this is the node we right clicked on to see its underlying transactions.
      const parentNode = isFetch ? params.parentNode : params.node;
      const firstFetch = params == null || parentNode == null || parentNode.data == null;
      const level = firstFetch ? 0 : parentNode.data.level + 1;
      reqParam.level = level;

      // Current Dim
      reqParam.currentDim = firstFetch ? activeDims[0].code : parentNode.data.nextDim;

      // Dim Order
      const dimCodes = activeDims.map((dim) => dim.code);
      reqParam.dimCodes = dimCodes;

      // Trend Dims - deduped union of trend dim codes from the active trend settings
      const trendDims = [...new Set((activeTrendSettings?.trends ?? []).flatMap((trend) => trend.dims))];
      if (trendDims.length > 0) {
        reqParam.trendDims = trendDims;

        // Trend Value Groups - per trended dim, the { key, label, ids } merges the backend uses to
        // build the trend CASE grouping expression (CASE WHEN id IN (...) THEN key ... ELSE id).
        // Only forward groups for dims actually being trended.
        const trendValueGroups = {};
        trendDims.forEach((dimCode) => {
          const groups = activeTrendSettings.trendValueGroups?.[dimCode];
          if (groups?.length) trendValueGroups[dimCode] = groups;
        });
        if (Object.keys(trendValueGroups).length > 0) {
          reqParam.trendValueGroups = trendValueGroups;
        }
      }

      // The ~ delimited path of nodes. The children of this parent/path are being requested
      let wherePath = firstFetch ? '%' : parentNode.data.path + '~%';
      if (parentNode.data?.isLeaf) {
        // This can only be true in the scenario of navigating to the Viewer
        wherePath = wherePath.substring(0, wherePath.length - 2);
      }
      reqParam.wherePath = wherePath;

      // Periodicities
      reqParam.dimToPeriodicities = dimToPeriodicities;

      // Tree Filtering
      let expr = null;
      activeDims.forEach((dim) => {
        const selectedIds = dimFilters[dim.code];
        const { leafIds } = TreeUtils.parseTree(dim.tree);

        // If all IDs are selected, there is no need to filter
        if (selectedIds.length !== leafIds.length) {
          const treeExpr = ExprUtils.whereParams(StringUtils.toLowerCamel(dim.code) + 'Id', selectedIds);
          expr = expr ? ExprUtils.and(expr, treeExpr) : treeExpr;
        }
      });

      // Trend column filter: when navigating (e.g. right-click) from a trend cell, narrow the
      // request to that cell's trend dim/value(s) so drill-through / viewers only see matching
      // transactions. Only the navigation path supplies params.column; the row-fetch path does not.
      const colDef = params.column?.getColDef?.();
      if (colDef?.trends) {
        Object.entries(colDef.trends).forEach(([dimCode, valueId]) => {
          const trendExpr = ExprUtils.whereParams(StringUtils.toLowerCamel(dimCode) + 'Id', valueId);
          expr = expr ? ExprUtils.and(expr, trendExpr) : trendExpr;
        });
      }
      reqParam.expr = expr;

      // Sorting
      const sortModel = params.request?.sortModel;
      if (sortModel) {
        const userSorter = sortModel[0];
        reqParam.userSorter = userSorter;
      }

      if (augReqParam) {
        augReqParam(reqParam);
      }

      return reqParam;
    },
    [activeView, activeDims, activeTrendSettings, dimFilters, dimToPeriodicities, augReqParam],
  );

  // ----- NavState -----

  const saveNavState = () => {
    if (!perspectiveDef) return;
    const newPerspectiveNavState = {
      viewName: activeView.name,
      dimCodes: activeDims.map((dim) => dim.code),
      dimFilters,
      dimToPeriodicities,
      trendSettings: activeTrendSettings,
      activeKpis,
      showFilterSummary,
      splitSizes,
    };

    assign(perspectiveNavStateId, newPerspectiveNavState);
  };

  useEffect(() => {
    saveNavState();
  }, [
    activeView,
    activeDims,
    dimFilters,
    dimToPeriodicities,
    activeTrendSettings,
    activeKpis,
    showFilterSummary,
    splitSizes,
  ]);

  // ----- View -----

  useEffect(() => {
    if (!perspectiveDef) return; // Only needs to be called when the view changes
    resetView();
  }, [activeView]);

  // FDN-53 (Reset View)
  const resetView = () => {
    gridRef.current.resetOpenedRowGroups();
    resetDims();
    resetPeriodicities();
    resetTrendSettings();
    resetKpis();

    if (onViewReset) {
      onViewReset();
    }
  };

  const changeView = (view) => {
    setActiveView(view);
  };

  // ----- Dimensions -----

  const setDims = (dimCodes) => {
    const prevDimCodes = activeDims.map((dim) => dim.code);

    const newDimFilters = {};
    dimCodes.forEach((dimCode) => {
      newDimFilters[dimCode] =
        prevDimCodes.includes(dimCode) ?
          dimFilters[dimCode]
        : TreeUtils.parseTree(perspectiveDef.codeToDim[dimCode].tree).leafIds;
    });

    setActiveDims(dimCodes.map((dimCode) => perspectiveDef.codeToDim[dimCode]));
    setDimFilters(newDimFilters);
  };

  const resetDims = () => {
    const activeDims = defaultDims();
    setActiveDims(activeDims);

    const dimFilters = defaultDimFilters();
    setDimFilters(dimFilters);
  };

  const defaultDims = () => {
    return activeView.dims.map((dimCode) => perspectiveDef.codeToDim[dimCode]);
  };

  const defaultDimFilters = () => {
    const dims = defaultDims();

    const dimFilters = {};
    dims.forEach((dim) => {
      const leafIds = defaultDimFiltersProp[dim.code] ?? TreeUtils.parseTree(dim.tree).leafIds;
      dimFilters[dim.code] = leafIds;
    });

    return dimFilters;
  };

  // ----- Periodicities -----

  const resetPeriodicities = () => {
    if (!activeView.dimToPeriodicities) return;

    const dimToPeriodicities = {};
    Object.entries(activeView.dimToPeriodicities).forEach(([dimCode, periodicities]) => {
      dimToPeriodicities[dimCode] = [periodicities[periodicities.length - 1]];
    });

    setDimToPeriodicities(dimToPeriodicities);
  };

  // ----- Trend -----

  const resetTrendSettings = () => {
    setActiveTrendSettings({
      trends: activeView.trends ?? [],
      trendValueGroups: activeView.trendValueGroups ?? {},
      trendValueGroupBy: activeView.trendValueGroupBy ?? {},
    });
  };

  // ----- KPIs -----

  const resetKpis = () => {
    setActiveKpis(activeView.kpis);
  };

  // ----- Context Menu Items -----

  const getContextMenuItems = (params) => {
    const contextMenuItems = [
      {
        name: 'Copy with Headers',
        action: () => copyWithHeaders(),
        icon: fas.faCopy,
      },
    ];

    return contextMenuItems;
  };

  const copyWithHeaders = () => {
    gridRef.current.gridApiRef.current.copySelectedRangeToClipboard({
      includeGroupHeaders: true,
      includeHeaders: true,
    });
  };

  const { openDialog } = useDialog();

  // ----- API to Parent -----

  // This had to be moved down due to the dependency on buildReqParam
  useImperativeHandle(ref, () => {
    return {
      getContextMenuItems,
      buildReqParam,
      codeToKpi: perspectiveDef?.codeToKpi,
      activeView,
      gridRef: gridRef,
      setDimFilter: (dimCode, ids) => {
        setDimFilters((prev) => ({ ...prev, [dimCode]: ids }));
      },
      navigateToViewer: (params, trxGroup, title, pluginCode = 'PERSPECTIVE_TRX_VIEWER_PLUGIN') => {
        const navState = {
          title,
          entity,
          pluginCode,
          viewerReqParam: {
            trxGroup,
            fetchReqParam: buildReqParam(params),
          },
        };
        navigateTo(`/${module}/perspective-trx-viewer`, navState);
      },
      navigateToLookupViewer: (params, leftTrxGroup, leftTitle, rightTrxGroup, rightTitle) => {
        const navState = {
          leftTitle,
          rightTitle,
          entity,
          reqParam: {
            leftTrxGroup: leftTrxGroup,
            rightTrxGroup: rightTrxGroup,
            fetchReqParam: buildReqParam(params),
          },
        };
        navigateTo(`/${module}/perspective-trx-lookup-viewer`, navState);
      },
      navigateToPerspectiveAdj: (params, trxGroup, title, dataPluginCode) => {
        const navState = {
          title,
          fetchReqParam: buildReqParam(params),
          trxGroup,
          dataPluginCode: dataPluginCode,
        };
        navigateTo(`/${module}/perspective-adj`, navState);
      },
      navigateToPivot: (params, trxGroup, title) => {
        const navState = {
          entity,
          fetchReqParam: buildReqParam(params),
          trxGroup,
          title,
        };
        navigateTo(`/${module}/perspective-pivot`, navState);
      },
      navigateToEntityList: (params, routeToEntity, navState) => {
        navState.entity = routeToEntity;
        navState.reqParam = { ...navState.reqParam, fetchReqParam: buildReqParam(params) };

        navigateTo(`/${module}/${StringUtils.toKebabConvention(routeToEntity)}`, navState);
      },
      overwrite: (params, kpiCode) => {
        const augFieldDefs = (fieldToFieldDef) => {
          fieldToFieldDef.targetKpi.component = (
            <TargetKpiSelect codeToKpi={perspectiveDef.codeToKpi} fieldDef={fieldToFieldDef.targetKpi} />
          );
        };

        const kpiDisplayName = perspectiveDef.codeToKpi[kpiCode].displayName;

        openDialog(ActionDialog, {
          data: {
            title: `${kpiDisplayName} Overwrite`,
            pluginCode: 'OVERWRITE_ACTION_PLUGIN',
            entity,
            augFieldDefs,
            addlReqData: {
              fetchReqParam: buildReqParam(params),
              adjKpi: kpiCode,
            },
          },
        });
      },
      openActionDialog: (params, title, pluginCode) => {
        openDialog(ActionDialog, {
          data: {
            title,
            pluginCode,
            entity,
            addlReqData: {
              fetchReqParam: buildReqParam(params),
              nodeData: params.node.data,
            },
          },
        });
      },
      openExportDialog: (data, displayName) => {
        openDialog(ExportDialog, {
          data: { entity, fetchReqParam: buildReqParam(data), uiType: 'PERSPECTIVE_GRID', displayName },
        });
      },
    };
  }, [buildReqParam, activeView]);

  const gridKey = useDependentKey(fieldDefs);

  // ----- Rendering -----
  if (!perspectiveDef) return null;

  let buttons = [];
  if (views.length > 1) {
    buttons.push(<ViewSelect key="viewSelect" views={views} activeView={activeView} onViewChange={changeView} />);
  }
  if (augButtons) augButtons(buttons);
  buttons.push(
    <TitleButton
      key="filterSummaryButton"
      icon={far.faFilter}
      toolTip="Open Filter Summary"
      onClick={() => setShowFilterSummary((prev) => !prev)}
    />,
  );
  if (activeView.dimsRearrangeable ?? true) {
    buttons.push(
      <DimRearrangeButton
        key="dimRearrangeButton"
        setDims={setDims}
        codeToDim={perspectiveDef.codeToDim}
        activeDims={activeDims}
        numFrozenDims={activeView.numFrozenDims}
      />,
    );
  }
  buttons.push(
    <TrendDimsButton
      key="trendDimsButton"
      codeToDim={perspectiveDef.codeToDim}
      codeToKpi={perspectiveDef.codeToKpi}
      kpiCodes={activeView.kpis}
      trendSettings={activeTrendSettings}
      trendValues={trendValues ?? {}}
      activeKpis={activeKpis}
      setTrendSettings={setActiveTrendSettings}
      setActiveKpis={setActiveKpis}
    />,
  );
  buttons.push(
    <TitleButton key="resetViewButton" icon={far.faWindowRestore} toolTip="Reset View" onClick={resetView} />,
  );

  buttons = [...buttons, <ExportButton key="export" exportReq={exportReq} />];

  return (
    <ContentBox>
      <EntityTitle buttons={buttons} />
      <EntityBanner />
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Dimension Filter Trees */}
        {activeDims.map((dim) => {
          const dimCode = dim.code;
          const selectedIds = dimFilters[dimCode];

          return (
            <DimTree
              key={dim.id}
              displayName={dim.displayName}
              disabled={dim.disabled}
              treeData={dim.tree}
              selectedIds={selectedIds}
              onFilterChange={(ids) => {
                setDimFilters((prev) => ({ ...prev, [dimCode]: ids }));
                events?.onDimFilterChange?.({ dimCode, ids });
              }}
              periodicities={activeView.dimToPeriodicities ? activeView.dimToPeriodicities[dim.code] : undefined}
              selectedPeriodicities={dimToPeriodicities ? dimToPeriodicities[dim.code] : undefined}
              onPeriodicityChange={(periodicities) =>
                setDimToPeriodicities((prev) => ({ ...prev, [dimCode]: periodicities }))
              }
            />
          );
        })}
      </Box>
      {fieldDefs && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Split
            direction="horizontal"
            initialSplit={splitSizes[0]}
            minSize={200}
            onDragEnd={({ 0: left, 1: right }) => setSplitSizes([left, right])}
            showSecondChild={showFilterSummary}
          >
            <Grid
              ref={gridRef}
              key={gridKey}
              gridOptions={{
                fieldDefs: fieldDefs,
                statusBar: null,
                rowModelType: 'serverSide',
                treeData: true,
                isServerSideGroup,
                getServerSideGroupKey,
                getContextMenuItems: getContextMenuItems,
                ...gridOptions,
              }}
              events={{
                ...events,
                onGridReady: onGridReady,
              }}
            />
            <FilterSummary activeDims={activeDims} dimFilters={dimFilters} showFilterSummary={showFilterSummary} />
          </Split>
        </Box>
      )}
    </ContentBox>
  );
};
